import { Fragment, useEffect, useState } from "react";
import { masterApi, apiError, formatBRL, formatL, PRODUTOS_LABEL } from "@/lib/api";
import { ExcelBtn } from "@/lib/excelDownload";
import { useUnit } from "@/pages/Master/MasterApp";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { comPagina, normalizaPaginado } from "@/lib/paginacao";
import { inicioDiaBR, fimDiaBR } from "@/lib/periodoBR";
import Pagination from "@/components/crud/Pagination";
import { Car, AlertTriangle, Zap } from "lucide-react";

export default function MasterRelatorios() {
  const { unitId } = useUnit();
  const [descs, setDescs] = useState([]);
  const [frentistas, setFrentistas] = useState([]);
  const [conversao, setConversao] = useState(null);
  const [auditoria, setAuditoria] = useState([]);
  const [codigos, setCodigos] = useState(null);
  const [vendasCategoria, setVendasCategoria] = useState(null);
  const [vendasCombustivel, setVendasCombustivel] = useState(null);
  const [consumoGrupos, setConsumoGrupos] = useState(null);
  const [vendasDesconto, setVendasDesconto] = useState(null);
  const [usoDesconto, setUsoDesconto] = useState(null);
  const [multiAbast, setMultiAbast] = useState(null);
  const [periodoInicio, setPeriodoInicio] = useState("");
  const [periodoFim, setPeriodoFim] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("all");
  const [minAbastDia, setMinAbastDia] = useState(2);
  // Item 1 (19/10): os dois relatórios sem limite de linhas são paginados (?page).
  const [pagAud, setPagAud] = useState({ page: 1, total_pages: 0, total: 0 });

  const aplicaAuditoria = (data, page) => {
    const pg = normalizaPaginado(data, page);
    setAuditoria(pg.items);
    setPagAud({ page: pg.page, total_pages: pg.total_pages, total: pg.total });
  };
  const aplicaMulti = (data, page) => {
    const pg = normalizaPaginado(data, page);
    // total = ocorrências do filtro inteiro (paginado) ou da lista toda (formato antigo).
    setMultiAbast({ ...pg, total_registros: pg.total, min_abast: pg.min_abast ?? minAbastDia });
  };

  const filtros = () => {
    const unit = unitId !== "all" ? `unit_id=${unitId}` : "";
    const periodo = [];
    // Dia de Brasília (00:00 a 23:59:59.999999), não 00:00 UTC (= 21h do dia anterior): lib/periodoBR.
    const ini = inicioDiaBR(periodoInicio);
    const fimP = fimDiaBR(periodoFim);
    if (ini) periodo.push(`inicio=${encodeURIComponent(ini)}`);
    if (fimP) periodo.push(`fim=${encodeURIComponent(fimP)}`);
    const qsBase = [unit, ...periodo].filter(Boolean).join("&");
    const qsVD = [qsBase, filtroCategoria !== "all" ? `categoria=${filtroCategoria}` : ""].filter(Boolean).join("&");
    // Janela em dias para os relatórios novos: se houver período, calcula dias entre as datas; senão usa 30
    let dias = 30;
    if (periodoInicio && periodoFim) {
      const ms = new Date(periodoFim).getTime() - new Date(periodoInicio).getTime();
      dias = Math.max(1, Math.ceil(ms / (1000 * 60 * 60 * 24)) + 1);
    } else if (periodoInicio) {
      const ms = Date.now() - new Date(periodoInicio).getTime();
      dias = Math.max(1, Math.ceil(ms / (1000 * 60 * 60 * 24)));
    }
    const qsMulti = [qsBase, `min_abast=${minAbastDia}`, !periodoInicio && !periodoFim ? `dias=${dias}` : ""].filter(Boolean).join("&");
    return { unit, qsBase, qsVD, dias, qsMulti };
  };

  const loadMulti = async (page) => {
    try {
      const { data } = await masterApi.get(comPagina(`/master/relatorios/clientes-multi-abast?${filtros().qsMulti}`, page));
      aplicaMulti(data, page);
    } catch (e) { modal.error(apiError(e)); }
  };
  const loadAuditoria = async (page) => {
    try {
      const { data } = await masterApi.get(comPagina(`/master/relatorios/auditoria-excecoes?${filtros().unit}`, page));
      aplicaAuditoria(data, page);
    } catch (e) { modal.error(apiError(e)); }
  };

  const load = async () => {
    const { unit, qsBase, qsVD, dias, qsMulti } = filtros();
    try {
      const [d, f, c, a, cod, vc, vcb, vd, ud, ma] = await Promise.all([
        masterApi.get(`/master/relatorios/descontos?${unit}`),
        masterApi.get(`/master/relatorios/frentistas?${unit}`),
        masterApi.get(`/master/relatorios/conversao-aditivada?${unit}`),
        masterApi.get(comPagina(`/master/relatorios/auditoria-excecoes?${unit}`, 1)),
        masterApi.get(`/master/relatorios/codigos-nao-usados`),
        masterApi.get(`/master/relatorios/vendas-por-categoria?${qsBase}`),
        masterApi.get(`/master/relatorios/vendas-por-combustivel?${qsVD}`),
        masterApi.get(`/master/relatorios/vendas-por-desconto?${qsVD}`),
        masterApi.get(`/master/relatorios/frentista-uso-desconto?${unit}&dias=${dias}`),
        masterApi.get(comPagina(`/master/relatorios/clientes-multi-abast?${qsMulti}`, 1)),
      ]);
      setDescs(d.data); setFrentistas(f.data); setConversao(c.data); aplicaAuditoria(a.data, 1); setCodigos(cod.data);
      setVendasCategoria(vc.data); setVendasCombustivel(vcb.data); setVendasDesconto(vd.data);
      setUsoDesconto(ud.data); aplicaMulti(ma.data, 1);
      // v2.29 — consumo dos grupos de consumo por produto (mesmos filtros de período/categoria/unidade)
      masterApi.get(`/master/relatorios/consumo-grupos?${qsVD}`)
        .then((r) => setConsumoGrupos(r.data)).catch(() => setConsumoGrupos(null));
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [unitId, periodoInicio, periodoFim, filtroCategoria, minAbastDia]);

  const unitQ = unitId !== "all" ? `?unit_id=${unitId}` : "";

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-5 float-in" data-testid="master-relatorios">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="chip mb-1.5 text-[10px] py-0.5">Análises</div>
          <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Relatórios</h1>
        </div>
        <ExcelBtn testid="excel-atendimentos" url={`/master/relatorios/atendimentos/excel${unitQ}`}
                  filename="atendimentos_completos.xlsx" label="Excel atendimentos" />
      </div>

      {/* Filtros de período + categoria — afetam os relatórios novos abaixo */}
      <div className="card-soft rounded-xl p-3 flex flex-wrap gap-3 items-end" data-testid="rel-filters">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Início</label>
          <input type="date" data-testid="rel-inicio" value={periodoInicio}
                 onChange={(e) => setPeriodoInicio(e.target.value)}
                 className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Fim</label>
          <input type="date" data-testid="rel-fim" value={periodoFim}
                 onChange={(e) => setPeriodoFim(e.target.value)}
                 className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Categoria (filtra "por desconto")</label>
          <select data-testid="rel-categoria" value={filtroCategoria}
                  onChange={(e) => setFiltroCategoria(e.target.value)}
                  className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm">
            <option value="all">Todas</option>
            <option value="normal">Normal</option>
            <option value="app_driver">Motorista de App</option>
          </select>
        </div>
        <button onClick={() => { setPeriodoInicio(""); setPeriodoFim(""); setFiltroCategoria("all"); }}
                className="btn-ghost h-9 px-3 text-[11px] font-medium">Limpar</button>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Mín. abast/dia (alerta)</label>
          <input type="number" min={2} max={20} data-testid="rel-min-abast" value={minAbastDia}
                 onChange={(e) => setMinAbastDia(Math.max(2, parseInt(e.target.value) || 2))}
                 className="mt-1 h-9 w-24 rounded-md bg-white border border-neutral-200 px-2 text-sm" />
        </div>
      </div>

      {vendasCategoria && (
        <Section title="Vendas por categoria de cliente">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <Th>Categoria</Th><Th>Atendimentos</Th><Th>Clientes únicos</Th>
                <Th>Litros</Th><Th>Faturamento líquido</Th><Th>Descontos</Th>
                <Th>Ticket médio</Th><Th>Share %</Th>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vendasCategoria.items.map((i) => (
                <TableRow key={i.categoria} data-testid={`vc-row-${i.categoria}`}>
                  <TableCell className="font-medium">
                    {i.categoria === "app_driver"
                      ? <span className="chip text-[10px] py-0.5" style={{ background: "rgba(217,119,6,0.18)", color: "#92400E" }}><Car size={11} />Motorista de App</span>
                      : <span className="chip text-[10px] py-0.5">Normal</span>}
                  </TableCell>
                  <TableCell>{i.atendimentos}</TableCell>
                  <TableCell>{i.clientes_unicos}</TableCell>
                  <TableCell>{formatL(i.litros)}</TableCell>
                  <TableCell className="font-semibold">{formatBRL(i.faturamento_liquido)}</TableCell>
                  <TableCell className="text-rose-600">{formatBRL(i.desconto_total)}</TableCell>
                  <TableCell>{formatBRL(i.ticket_medio)}</TableCell>
                  <TableCell>{i.share_pct}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="text-[11px] text-neutral-500 mt-2 px-2">Total: <b>{formatBRL(vendasCategoria.total_faturamento)}</b></div>
        </Section>
      )}

      {vendasCombustivel && (
        <Section title="Vendas por combustível (litros, R$ e descontos)">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <Th>Combustível</Th><Th>Atendimentos</Th><Th>Litros</Th>
                <Th>Faturamento bruto</Th><Th>Faturamento líquido</Th><Th>Descontos</Th>
                <Th>Preço médio/L</Th><Th>Share litros %</Th>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vendasCombustivel.items.map((i) => (
                <TableRow key={i.produto} data-testid={`vcb-row-${i.produto}`}>
                  <TableCell className="font-medium">{PRODUTOS_LABEL[i.produto] || i.produto}</TableCell>
                  <TableCell>{i.atendimentos}</TableCell>
                  <TableCell>{formatL(i.litros)}</TableCell>
                  <TableCell>{formatBRL(i.valor_bruto)}</TableCell>
                  <TableCell className="font-semibold">{formatBRL(i.valor_liquido)}</TableCell>
                  <TableCell className="text-rose-600">{formatBRL(i.desconto_total)}</TableCell>
                  <TableCell>{formatBRL(i.preco_medio_liquido)}</TableCell>
                  <TableCell>{i.share_litros_pct}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="text-[11px] text-neutral-500 mt-2 px-2">
            Totais: <b>{formatL(vendasCombustivel.totais?.litros || 0)}</b> · líquido <b>{formatBRL(vendasCombustivel.totais?.valor_liquido || 0)}</b> · descontos <b className="text-rose-600">{formatBRL(vendasCombustivel.totais?.desconto_total || 0)}</b>
          </div>
          {unitId === "all" && (vendasCombustivel.por_unidade || []).map((u) => (
            <div key={u.unit_id} className="mt-3" data-testid={`vcb-unit-${u.unit_id}`}>
              <div className="text-[12px] font-semibold px-2 mb-1">
                {u.unit_nome} <span className="text-neutral-400 font-normal">— {formatL(u.totais.litros)} · líquido {formatBRL(u.totais.valor_liquido)} · descontos {formatBRL(u.totais.desconto_total)}</span>
              </div>
              <Table>
                <TableBody>
                  {u.combustiveis.map((i) => (
                    <TableRow key={i.produto}>
                      <TableCell className="font-medium">{PRODUTOS_LABEL[i.produto] || i.produto}</TableCell>
                      <TableCell>{i.atendimentos}</TableCell>
                      <TableCell>{formatL(i.litros)}</TableCell>
                      <TableCell>{formatBRL(i.valor_bruto)}</TableCell>
                      <TableCell className="font-semibold">{formatBRL(i.valor_liquido)}</TableCell>
                      <TableCell className="text-rose-600">{formatBRL(i.desconto_total)}</TableCell>
                      <TableCell>{formatBRL(i.preco_medio_liquido)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </Section>
      )}

      {consumoGrupos && (
        <Section title="Consumo dos grupos de consumo por produto">
          {consumoGrupos.items.length === 0 ? (
            <div className="text-[12px] text-neutral-500 px-2 py-3" data-testid="cg-vazio">
              Nenhuma venda de clientes vinculados a grupos de consumo no período/filtro selecionado.
            </div>
          ) : (
            <Table data-testid="cg-tabela">
              <TableHeader>
                <TableRow className="bg-neutral-50">
                  <Th>Grupo / produto</Th><Th>Atendimentos</Th><Th>Litros</Th>
                  <Th>Faturamento bruto</Th><Th>Faturamento líquido</Th><Th>Descontos</Th><Th>Preço médio/L</Th>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consumoGrupos.items.map((g) => (
                  <Fragment key={g.grupo_id}>
                    <TableRow className="bg-blue-50/40" data-testid={`cg-grupo-${g.grupo_id}`}>
                      <TableCell className="font-semibold">
                        {g.nome}
                        <span className="ml-1.5 text-[10px] font-normal text-neutral-500">
                          {g.categoria === "app_driver" ? "Motorista de App" : g.categoria === "normal" ? "Cliente Normal" : ""}
                          {` · ${g.clientes} cliente${g.clientes === 1 ? "" : "s"}`}
                        </span>
                      </TableCell>
                      <TableCell className="font-semibold">{g.atendimentos}</TableCell>
                      <TableCell className="font-semibold">{formatL(g.litros)}</TableCell>
                      <TableCell className="font-semibold">{formatBRL(g.valor_bruto)}</TableCell>
                      <TableCell className="font-semibold">{formatBRL(g.valor_liquido)}</TableCell>
                      <TableCell className="font-semibold text-rose-600">{formatBRL(g.desconto_total)}</TableCell>
                      <TableCell className="font-semibold">{formatBRL(g.preco_medio_liquido)}</TableCell>
                    </TableRow>
                    {g.produtos.map((i) => (
                      <TableRow key={`${g.grupo_id}-${i.produto}`}>
                        <TableCell className="pl-6">{PRODUTOS_LABEL[i.produto] || i.produto}</TableCell>
                        <TableCell>{i.atendimentos}</TableCell>
                        <TableCell>{formatL(i.litros)}</TableCell>
                        <TableCell>{formatBRL(i.valor_bruto)}</TableCell>
                        <TableCell>{formatBRL(i.valor_liquido)}</TableCell>
                        <TableCell className="text-rose-600">{formatBRL(i.desconto_total)}</TableCell>
                        <TableCell>{formatBRL(i.preco_medio_liquido)}</TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
                <TableRow className="bg-neutral-50">
                  <TableCell colSpan={7} className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                    Consumo global por produto — todos os grupos
                  </TableCell>
                </TableRow>
                {consumoGrupos.total_por_produto.map((i) => (
                  <TableRow key={`tot-${i.produto}`} data-testid={`cg-total-${i.produto}`}>
                    <TableCell className="font-medium">{PRODUTOS_LABEL[i.produto] || i.produto}</TableCell>
                    <TableCell>{i.atendimentos}</TableCell>
                    <TableCell>{formatL(i.litros)}</TableCell>
                    <TableCell>{formatBRL(i.valor_bruto)}</TableCell>
                    <TableCell className="font-semibold">{formatBRL(i.valor_liquido)}</TableCell>
                    <TableCell className="text-rose-600">{formatBRL(i.desconto_total)}</TableCell>
                    <TableCell>{formatBRL(i.preco_medio_liquido)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="text-[11px] text-neutral-500 mt-2 px-2">
            A venda conta para o grupo em que o cliente estava no momento do abastecimento. Troca de óleo não entra.
            {consumoGrupos.items.length > 0 && <> Total dos grupos: <b>{formatL(consumoGrupos.total.litros)}</b> · líquido <b>{formatBRL(consumoGrupos.total.valor_liquido)}</b> · descontos <b className="text-rose-600">{formatBRL(consumoGrupos.total.desconto_total)}</b></>}
          </div>
        </Section>
      )}

      {vendasDesconto && (
        <Section title={`Vendas por desconto ${filtroCategoria !== "all" ? `(${filtroCategoria === "app_driver" ? "Motorista de App" : "Normal"})` : ""}`}>
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <Th>Tipo</Th><Th>Atend.</Th><Th>Clientes</Th>
                <Th>Litros</Th><Th>Faturamento</Th><Th>Descontos</Th><Th>Share %</Th>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vendasDesconto.items.map((i) => (
                <TableRow key={i.tipo}>
                  <TableCell className="font-medium">{i.tipo}</TableCell>
                  <TableCell>{i.atendimentos}</TableCell>
                  <TableCell>{i.clientes_unicos}</TableCell>
                  <TableCell>{formatL(i.litros)}</TableCell>
                  <TableCell>{formatBRL(i.faturamento_liquido)}</TableCell>
                  <TableCell className="text-rose-600">{formatBRL(i.desconto_total)}</TableCell>
                  <TableCell>{i.share_pct}%</TableCell>
                </TableRow>
              ))}
              {vendasDesconto.items.length === 0 && <Empty cols={7} />}
            </TableBody>
          </Table>
          <div className="text-[11px] text-neutral-500 mt-2 px-2">Total: <b>{formatBRL(vendasDesconto.total_faturamento)}</b> em {vendasDesconto.total_atendimentos} atendimentos</div>
        </Section>
      )}

      {/* AUDITORIA — Uso de desconto por vendedor */}
      {usoDesconto && (
        <Section title="Uso de desconto por vendedor (auditoria de fraude)">
          <div className="text-[11px] text-neutral-600 mb-2 px-1">
            % de abastecimentos com desconto aplicado em cada vendedor nos últimos <b>{usoDesconto.janela_dias} dias</b>.
            Taxa média geral: <b>{usoDesconto.taxa_media_global_pct}%</b> ({usoDesconto.total_atendimentos_periodo} atendimentos).
            Vendedores com taxa MUITO acima da média podem estar aplicando descontos indevidamente.
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <Th>Vendedor</Th><Th>Unidade</Th><Th>Atendimentos</Th>
                <Th>Com desconto</Th><Th>% com desconto</Th>
                <Th>Desconto total (R$)</Th><Th>Desconto médio/atd</Th>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usoDesconto.items.map((i) => {
                const acimaMedia = usoDesconto.taxa_media_global_pct > 0
                  && i.taxa_desconto_pct >= usoDesconto.taxa_media_global_pct * 1.3
                  && i.total_atendimentos >= 10;
                const muitoAcima = usoDesconto.taxa_media_global_pct > 0
                  && i.taxa_desconto_pct >= usoDesconto.taxa_media_global_pct * 1.6
                  && i.total_atendimentos >= 10;
                return (
                  <TableRow key={i.frentista_id} data-testid={`uso-desc-${i.frentista_username || i.frentista_id?.slice(0, 6)}`}>
                    <TableCell className="font-medium">{i.frentista_nome}</TableCell>
                    <TableCell className="text-xs">{i.unit_nome}</TableCell>
                    <TableCell>{i.total_atendimentos}</TableCell>
                    <TableCell>{i.atd_com_desconto}</TableCell>
                    <TableCell>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                        muitoAcima ? "bg-rose-100 text-rose-800"
                        : acimaMedia ? "bg-amber-100 text-amber-800"
                        : "bg-emerald-50 text-emerald-700"
                      }`}>
                        {i.taxa_desconto_pct}%
                        {muitoAcima && <AlertTriangle size={11} className="inline-block ml-1 -mt-0.5" />}
                        {!muitoAcima && acimaMedia && <Zap size={11} className="inline-block ml-1 -mt-0.5" />}
                      </span>
                    </TableCell>
                    <TableCell className="text-rose-600">{formatBRL(i.desconto_total_reais)}</TableCell>
                    <TableCell>{formatBRL(i.desconto_medio_atd)}</TableCell>
                  </TableRow>
                );
              })}
              {usoDesconto.items.length === 0 && <Empty cols={7} />}
            </TableBody>
          </Table>
          <div className="text-[10px] text-neutral-500 mt-2 px-1">
            <span className="inline-block w-2 h-2 rounded-full bg-rose-400 mr-1"></span> ≥1.6× a média ·
            <span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-1 ml-2"></span> ≥1.3× a média ·
            Considera apenas vendedores com ≥10 atendimentos no período.
          </div>
        </Section>
      )}

      {/* Clientes com 2+ abastecimentos no mesmo dia */}
      {multiAbast && (
        <Section title={`Clientes com ${multiAbast.min_abast}+ abastecimentos no mesmo dia`}>
          <div className="text-[11px] text-neutral-600 mb-2 px-1">
            Detecta clientes que abasteceram <b>{multiAbast.min_abast}× ou mais</b> em um único dia no período selecionado.
            Pode indicar fraude, revenda ou cadastros compartilhados. <b>{multiAbast.total_registros}</b> ocorrências encontradas.
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <Th>Data</Th><Th>Cliente</Th><Th>Telefone</Th><Th>Placa</Th>
                <Th>Categoria</Th><Th>Qtd</Th><Th>Litros</Th>
                <Th>Valor total</Th><Th>Vendedores</Th>
              </TableRow>
            </TableHeader>
            <TableBody>
              {multiAbast.items.map((i, idx) => (
                <TableRow key={`${i.cliente_id}-${i.dia}-${idx}`} data-testid={`multi-abast-${idx}`}>
                  <TableCell className="text-xs font-mono">{i.dia}</TableCell>
                  <TableCell className="font-medium">{i.cliente_nome || "?"}</TableCell>
                  <TableCell className="text-xs">{i.cliente_telefone}</TableCell>
                  <TableCell className="text-xs">{i.placa || "—"}</TableCell>
                  <TableCell>
                    {i.cliente_categoria === "app_driver"
                      ? <span className="chip text-[10px] py-0.5" style={{ background: "rgba(217,119,6,0.18)", color: "#92400E" }}><Car size={11} />App</span>
                      : <span className="chip text-[10px] py-0.5">Normal</span>}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                      i.qty_abastecimentos >= 4 ? "bg-rose-100 text-rose-800"
                      : i.qty_abastecimentos >= 3 ? "bg-amber-100 text-amber-800"
                      : "bg-blue-50 text-blue-700"
                    }`}>{i.qty_abastecimentos}×</span>
                  </TableCell>
                  <TableCell>{formatL(i.litros_total)}</TableCell>
                  <TableCell className="font-semibold">{formatBRL(i.valor_total_liquido)}</TableCell>
                  <TableCell className="text-xs">{i.frentistas.join(", ") || "—"}</TableCell>
                </TableRow>
              ))}
              {multiAbast.items.length === 0 && <Empty cols={9} />}
            </TableBody>
          </Table>
          <div className="mt-3">
            <Pagination page={multiAbast.page} totalPages={multiAbast.total_pages} total={multiAbast.total}
                        onPage={loadMulti} unitLabel="ocorrências" />
          </div>
          <div className="text-[10px] text-neutral-500 mt-2 px-1">
            {multiAbast.inicio && multiAbast.fim && (
              <>Período: <b>{new Date(multiAbast.inicio).toLocaleDateString("pt-BR")}</b> a <b>{new Date(multiAbast.fim).toLocaleDateString("pt-BR")}</b>. </>
            )}
            Ajuste o filtro "Mín. abast/dia" acima para mudar o threshold.
          </div>
        </Section>
      )}

      <Section title="Volume por Modalidade de Desconto"
               cta={<ExcelBtn testid="excel-descontos" url={`/master/relatorios/descontos/excel${unitQ}`} filename="relatorio_descontos.xlsx" />}>
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <Th>Tipo</Th><Th>Atend.</Th><Th>Litros</Th><Th>Faturamento</Th><Th>Desconto</Th>
            </TableRow>
          </TableHeader>
          <TableBody>
            {descs.map((d) => (
              <TableRow key={d.tipo}>
                <TableCell className="font-medium">{d.tipo}</TableCell>
                <TableCell>{d.count}</TableCell>
                <TableCell>{formatL(d.volume_litros)}</TableCell>
                <TableCell>{formatBRL(d.faturamento)}</TableCell>
                <TableCell className="text-rose-600">{formatBRL(d.desconto_total)}</TableCell>
              </TableRow>
            ))}
            {descs.length === 0 && <Empty cols={5} />}
          </TableBody>
        </Table>
      </Section>

      <Section title="Performance de Vendedores"
               cta={<ExcelBtn testid="excel-frentistas" url={`/master/relatorios/frentistas/excel${unitQ}`} filename="relatorio_frentistas.xlsx" />}>
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <Th>Vendedor</Th><Th>Vendas</Th><Th>Faturamento</Th><Th>Ticket</Th><Th>Litros</Th>
              <Th>Cad.</Th><Th>Logins</Th><Th>Nota</Th><Th>Serv. %</Th>
            </TableRow>
          </TableHeader>
          <TableBody>
            {frentistas.map((f) => (
              <TableRow key={f.frentista_id}>
                <TableCell className="font-medium">{f.username}</TableCell>
                <TableCell>{f.count}</TableCell>
                <TableCell>{formatBRL(f.faturamento)}</TableCell>
                <TableCell>{formatBRL(f.ticket_medio)}</TableCell>
                <TableCell>{formatL(f.litros)}</TableCell>
                <TableCell>{f.cadastros_novos}</TableCell>
                <TableCell>{f.logins}</TableCell>
                <TableCell>{f.nota_media ?? "—"} ({f.pesquisas_count})</TableCell>
                <TableCell>{f.servicos_ofereceu_pct}%</TableCell>
              </TableRow>
            ))}
            {frentistas.length === 0 && <Empty cols={9} />}
          </TableBody>
        </Table>
      </Section>

      <section className="grid md:grid-cols-2 gap-3">
        <div className="card-soft rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Conversão Comum → Aditivada</div>
          {conversao && (
            <div className="mt-1.5 space-y-0.5 text-xs">
              <div>Ofereceram: <b>{conversao.total_ofereceram}</b></div>
              <div>Converteram: <b>{conversao.convertidos}</b></div>
              <div className="text-3xl font-serif-display mt-2">{conversao.taxa_pct}%</div>
            </div>
          )}
        </div>
        <div className="card-soft rounded-xl p-4 bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="text-[10px] uppercase tracking-widest text-amber-700">Códigos de Indicação Não Utilizados</div>
          <div className="text-4xl font-serif-display mt-1.5 text-amber-900">{codigos?.codigos_nao_utilizados ?? 0}</div>
        </div>
      </section>

      <Section title="Auditoria · Liberação Especial"
               cta={<ExcelBtn testid="excel-auditoria" url={`/master/relatorios/auditoria-excecoes/excel${unitQ}`} filename="relatorio_auditoria.xlsx" />}>
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50"><Th>Data</Th><Th>Vendedor</Th><Th>Cliente</Th><Th>CPF</Th></TableRow>
          </TableHeader>
          <TableBody>
            {auditoria.map((a, idx) => (
              <TableRow key={idx}>
                <TableCell className="text-xs">{new Date(a.criado_em).toLocaleString("pt-BR")}</TableCell>
                <TableCell>{a.frentista_username || a.frentista_id?.slice(0, 8)}</TableCell>
                <TableCell>{a.cliente_nome}</TableCell>
                <TableCell className="text-xs">{a.cliente_cpf || "—"}</TableCell>
              </TableRow>
            ))}
            {auditoria.length === 0 && <Empty cols={4} />}
          </TableBody>
        </Table>
        <div className="mt-3">
          <Pagination page={pagAud.page} totalPages={pagAud.total_pages} total={pagAud.total}
                      onPage={loadAuditoria} unitLabel="registros" />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, cta, children }) {
  return (
    <section className="card-soft rounded-xl p-4">
      <div className="flex items-center justify-between mb-2.5 flex-wrap gap-2">
        <h2 className="font-serif-display text-lg">{title}</h2>
        {cta}
      </div>
      <div className="overflow-x-auto">{children}</div>
    </section>
  );
}
function Th({ children }) {
  return <TableHead className="text-[10px] uppercase tracking-widest">{children}</TableHead>;
}
function Empty({ cols }) {
  return <TableRow><TableCell colSpan={cols} className="text-center text-neutral-500 py-6">Sem dados.</TableCell></TableRow>;
}
